import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fmtDate, LEVEL_LABELS, fmtMoney } from "@/lib/api";
import { statusChip } from "@/components/StatusChip";
import { Check, AlertTriangle, ArrowRight, Send, CornerDownRight, Sparkles, MessageCircle, Twitter, Copy, Heart } from "lucide-react";
import { toast } from "sonner";

export default function ComplaintDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [composing, setComposing] = useState(null); // when re-opening compose / new level

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/complaints/${id}`);
            setData(r.data);
        } catch {
            toast.error("Could not load complaint");
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    if (!data) return <Layout><div className="speakup-card p-6 text-[var(--speakup-text-muted)]">Loading…</div></Layout>;

    const c = data.complaint;
    const sp = c.structured_payload || {};
    const entityName = data.entity?.name || c.entity_name_extracted || "Institution";

    const reopenCompose = async () => {
        try {
            const r = await api.post(`/complaints/${id}/approve`, { attested: true });
            setComposing(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not generate compose");
        }
    };

    return (
        <Layout>
            <Link to="/" className="text-sm text-[var(--speakup-text-muted)] hover:underline" data-testid="detail-back">
                ← All complaints
            </Link>

            <div className="mt-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[12px] uppercase tracking-wider text-[var(--speakup-text-muted)]">
                        {entityName} · Opened {fmtDate(c.created_at)}
                    </div>
                    <h1 className="display text-[26px] md:text-[30px] mt-1 leading-tight">{sp.summary || "Grievance"}</h1>
                    <div className="mt-2 flex flex-wrap gap-2 text-[13px] text-[var(--speakup-text-muted)] tabular">
                        {sp.issue_category && <span>· {sp.issue_category}</span>}
                        {sp.amount_disputed != null && <span>· {fmtMoney(sp.amount_disputed)}</span>}
                        {sp.account_or_policy_ref && <span>· {sp.account_or_policy_ref}</span>}
                    </div>
                </div>
                <div>{statusChip(c.status)}</div>
            </div>

            {composing ? (
                <ComposeCard composing={composing} onClose={() => { setComposing(null); load(); }} />
            ) : (
                <NextActionCard complaint={c} entity={data.entity} onReopenCompose={reopenCompose} onChanged={load} />
            )}

            <Timeline complaint={c} />

            <ReplyBox complaint={c} onChanged={load} />

            {c.status === "regulator_eligible" || c.current_level === 3 ? (
                <div className="mt-6">
                    <button
                        onClick={() => nav(`/c/${id}/regulator`)}
                        data-testid="detail-open-regulator-btn"
                        className="h-12 px-5 rounded-[12px] text-white font-semibold inline-flex items-center gap-2"
                        style={{ background: "var(--speakup-regulator)" }}
                    >
                        <Sparkles size={18} /> Open RBI Ombudsman co-pilot
                    </button>
                </div>
            ) : null}
        </Layout>
    );
}

function NextActionCard({ complaint: c, entity, onReopenCompose, onChanged }) {
    const status = c.status;
    if (status === "drafted" || status === "ready_to_send") {
        return (
            <Action
                icon={<Send size={20} />}
                title="Send the draft from your email"
                body="Tap below to reopen your email with the prefilled complaint."
                cta="Open compose"
                onClick={onReopenCompose}
                testId="action-open-compose"
            />
        );
    }
    if (status === "sent_verified" || status === "sent_unverified" || status === "awaiting_reply") {
        return (
            <div className="speakup-card p-5 md:p-6 mt-5" data-testid="action-have-you-sent">
                <HaveYouSent complaint={c} onChanged={onChanged} />
            </div>
        );
    }
    if (status === "escalation_due") {
        return <EscalateCard complaint={c} entity={entity} onChanged={onChanged} />;
    }
    if (status === "resolved") {
        return <ResolvedShareCard complaint={c} />;
    }
    return null;
}

function Action({ icon, title, body, cta, onClick, tone, testId }) {
    const bg = tone === "resolved" ? "rgba(22,163,74,0.08)" : "rgba(242,59,54,0.06)";
    const border = tone === "resolved" ? "rgba(22,163,74,0.25)" : "rgba(242,59,54,0.25)";
    return (
        <div
            className="mt-5 rounded-[14px] p-5 md:p-6 flex items-start gap-4"
            style={{ background: bg, border: `1px solid ${border}` }}
            data-testid={testId}
        >
            <div className="h-10 w-10 rounded-full bg-white border border-[var(--speakup-border)] flex items-center justify-center text-[var(--speakup-red)] shrink-0">
                {icon}
            </div>
            <div className="flex-1">
                <div className="font-semibold text-[var(--speakup-text-strong)]">{title}</div>
                <div className="text-[14px] text-[var(--speakup-text-muted)] mt-1">{body}</div>
                {cta && (
                    <button
                        onClick={onClick}
                        className="mt-3 h-11 px-4 rounded-[10px] text-white font-semibold"
                        style={{ background: "var(--speakup-red)" }}
                        data-testid={`${testId}-btn`}
                    >
                        {cta}
                    </button>
                )}
            </div>
        </div>
    );
}

function ResolvedShareCard({ complaint }) {
    const sp = complaint.structured_payload || {};
    const entity = complaint.entity_name_extracted || sp.entity_name || "the bank";
    const amount = sp.amount_disputed;
    const appUrl = (typeof window !== "undefined" && window.location?.origin) || "https://speakup.in";

    const defaultMessage = amount
        ? `${entity} reversed ₹${Number(amount).toLocaleString("en-IN")} for me — SpeakUp helped me draft the complaint and walk it through the escalation. Free tool: ${appUrl}`
        : `Got my issue with ${entity} resolved — SpeakUp drafted the complaint and walked me through the escalation. Free tool: ${appUrl}`;

    const [message, setMessage] = useState(defaultMessage);
    const [copied, setCopied] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [addToWall, setAddToWall] = useState(true);
    const [showAmount, setShowAmount] = useState(!!amount);
    const [winSubmitted, setWinSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
    const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`;

    const submitWin = async () => {
        if (winSubmitted) return;
        setSubmitting(true);
        try {
            await api.post(`/wins`, {
                complaint_id: complaint.id,
                show_amount: showAmount,
                note: null,
            });
            setWinSubmitted(true);
            toast.success("Added to the Wall of Wins. Thank you.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not add to Wall of Wins");
        } finally {
            setSubmitting(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(message);
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
            if (addToWall && !winSubmitted) submitWin();
        } catch {
            toast.error("Could not copy — please select and copy manually.");
        }
    };

    const openShare = async (url) => {
        // Fire-and-forget the win submission before navigating away
        if (addToWall && !winSubmitted) await submitWin();
        window.open(url, "_blank", "noopener");
    };

    if (dismissed) {
        return (
            <div
                className="mt-5 rounded-[14px] p-5 flex items-center gap-3"
                style={{
                    background: "rgba(22,163,74,0.08)",
                    border: "1px solid rgba(22,163,74,0.25)",
                }}
                data-testid="action-resolved-dismissed"
            >
                <Check size={20} className="text-[var(--speakup-resolved)] speakup-check" />
                <div className="font-semibold text-[var(--speakup-text-strong)]">
                    Resolved. Glad this worked out.
                </div>
            </div>
        );
    }

    return (
        <div
            className="mt-5 rounded-[14px] p-5 md:p-6"
            style={{
                background: "rgba(22,163,74,0.06)",
                border: "1px solid rgba(22,163,74,0.25)",
            }}
            data-testid="action-resolved-share-card"
        >
            <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-white border border-[var(--speakup-border)] flex items-center justify-center text-[var(--speakup-resolved)] shrink-0">
                    <Heart size={20} fill="currentColor" />
                </div>
                <div className="flex-1">
                    <div className="display text-[20px] text-[var(--speakup-text-strong)]">
                        You got it resolved.
                    </div>
                    <p className="text-[14px] text-[var(--speakup-text-muted)] mt-1">
                        Most Indians don't know they can do this. If SpeakUp helped, share your win — a friend with
                        the same problem will thank you.
                    </p>
                </div>
            </div>

            <div className="mt-4">
                <div className="text-[12px] uppercase tracking-wider text-[var(--speakup-text-muted)] mb-1.5">
                    Your message — edit anything
                </div>
                <textarea
                    rows={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    data-testid="resolved-share-message"
                    className="w-full p-3 rounded-[10px] border border-[var(--speakup-border)] bg-white text-[14px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[var(--speakup-resolved)]/40"
                />
            </div>

            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                <button
                    onClick={() => openShare(wa)}
                    data-testid="resolved-share-whatsapp-btn"
                    className="h-12 inline-flex items-center justify-center gap-2 rounded-[12px] text-white font-semibold"
                    style={{ background: "#25D366" }}
                >
                    <MessageCircle size={16} /> WhatsApp
                </button>
                <button
                    onClick={() => openShare(tw)}
                    data-testid="resolved-share-twitter-btn"
                    className="h-12 inline-flex items-center justify-center gap-2 rounded-[12px] text-white font-semibold"
                    style={{ background: "#0F1419" }}
                >
                    <Twitter size={16} /> Post on X
                </button>
                <button
                    onClick={copy}
                    data-testid="resolved-share-copy-btn"
                    className="h-12 inline-flex items-center justify-center gap-2 rounded-[12px] border border-[var(--speakup-border)] font-semibold bg-white"
                >
                    <Copy size={16} /> {copied ? "Copied ✓" : "Copy"}
                </button>
            </div>

            {/* Wall of Wins opt-in */}
            <div
                className="mt-5 pt-5 border-t border-[var(--speakup-border)]"
                data-testid="resolved-wall-of-wins-block"
            >
                <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={addToWall}
                        disabled={winSubmitted}
                        onChange={(e) => setAddToWall(e.target.checked)}
                        className="mt-1 h-5 w-5 accent-[var(--speakup-resolved)]"
                        data-testid="resolved-wall-of-wins-checkbox"
                    />
                    <div className="text-[14px]">
                        <div className="text-[var(--speakup-text-strong)] font-semibold">
                            Add this win anonymously to SpeakUp's Wall of Wins
                        </div>
                        <div className="text-[var(--speakup-text-muted)] mt-1">
                            We'll publish a masked entry — your <em>institution</em>, <em>issue type</em>, and{" "}
                            <em>days to resolve</em>. No phone, no name, no account/card numbers. Helps the next
                            person in your shoes find their way.
                        </div>
                    </div>
                </label>
                {addToWall && amount && !winSubmitted && (
                    <label className="flex items-center gap-3 mt-3 ml-8 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={showAmount}
                            onChange={(e) => setShowAmount(e.target.checked)}
                            data-testid="resolved-wall-of-wins-amount-checkbox"
                            className="h-4 w-4 accent-[var(--speakup-resolved)]"
                        />
                        <span className="text-[13px] text-[var(--speakup-text-muted)]">
                            Include the amount recovered (₹{Number(amount).toLocaleString("en-IN")})
                        </span>
                    </label>
                )}
                {winSubmitted ? (
                    <div
                        className="mt-3 flex items-center gap-2 text-[var(--speakup-resolved)] text-[14px] font-semibold"
                        data-testid="resolved-wall-of-wins-confirmed"
                    >
                        <Check size={16} className="speakup-check" /> Added to the Wall of Wins.
                    </div>
                ) : (
                    addToWall && (
                        <button
                            onClick={submitWin}
                            disabled={submitting}
                            data-testid="resolved-wall-of-wins-submit"
                            className="mt-3 h-10 px-4 rounded-[10px] text-white text-[14px] font-semibold disabled:opacity-60"
                            style={{ background: "var(--speakup-resolved)" }}
                        >
                            {submitting ? "Adding…" : "Add to Wall of Wins"}
                        </button>
                    )
                )}
            </div>

            <div className="mt-4 flex items-center justify-between">
                <p className="text-[12px] text-[var(--speakup-text-muted)]">
                    We never auto-post anything. Sharing just opens WhatsApp / X with your text.
                </p>
                <button
                    onClick={() => setDismissed(true)}
                    data-testid="resolved-share-dismiss-btn"
                    className="text-[13px] text-[var(--speakup-text-muted)] underline"
                >
                    Not now
                </button>
            </div>
        </div>
    );
}

function HaveYouSent({ complaint, onChanged }) {
    const [state, setState] = useState(
        complaint.send_provenance ? (complaint.proof_of_filing_ref ? "verified" : "unverified") : "ask"
    );
    const [refNo, setRefNo] = useState(complaint.proof_of_filing_ref || "");
    const [busy, setBusy] = useState(false);

    const markSent = async (withRef) => {
        setBusy(true);
        try {
            await api.post(`/complaints/${complaint.id}/mark-sent`, {
                reference_no: withRef ? refNo.trim() : null,
                send_date: new Date().toISOString(),
            });
            await onChanged();
            setState(withRef ? "verified" : "unverified");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const addProof = async () => {
        setBusy(true);
        try {
            await api.post(`/complaints/${complaint.id}/proof`, { reference_no: refNo.trim() });
            await onChanged();
            setState("verified");
            toast.success("Reference saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    if (state === "ask") {
        return (
            <div>
                <div className="text-[12px] uppercase tracking-wider text-[var(--speakup-text-muted)]">
                    Send confirmation
                </div>
                <div className="display text-[18px] mt-1">Have you sent it from your email?</div>
                <div className="text-[14px] text-[var(--speakup-text-muted)] mt-1">
                    If the hidden tracking copy didn't reach us in 24 hours, we'll need you to confirm.
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <input
                        placeholder="Acknowledgement / reference number (optional but recommended)"
                        value={refNo}
                        onChange={(e) => setRefNo(e.target.value)}
                        data-testid="have-sent-ref-input"
                        className="flex-1 min-w-[260px] h-11 px-3 rounded-[10px] border border-[var(--speakup-border)] tabular"
                    />
                    <button
                        onClick={() => markSent(true)}
                        disabled={busy || !refNo.trim()}
                        data-testid="have-sent-yes-with-ref-btn"
                        className="h-11 px-4 rounded-[10px] text-white font-semibold disabled:opacity-60"
                        style={{ background: "var(--speakup-red)" }}
                    >
                        Yes — with reference
                    </button>
                    <button
                        onClick={() => markSent(false)}
                        disabled={busy}
                        data-testid="have-sent-yes-no-ref-btn"
                        className="h-11 px-4 rounded-[10px] border border-[var(--speakup-border)] font-semibold"
                    >
                        Yes — no reference yet
                    </button>
                </div>
            </div>
        );
    }
    if (state === "unverified") {
        return (
            <div>
                <div className="flex items-center gap-2">
                    <AlertTriangle size={16} className="text-[var(--speakup-waiting)]" />
                    <div className="font-semibold text-[var(--speakup-text-strong)]">Reference number pending</div>
                </div>
                <p className="text-[14px] text-[var(--speakup-text-muted)] mt-1">
                    We can't reach the RBI Ombudsman without your bank's complaint reference. Add it whenever the
                    bank acknowledges your email.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                    <input
                        placeholder="Reference / complaint number"
                        value={refNo}
                        onChange={(e) => setRefNo(e.target.value)}
                        data-testid="add-proof-ref-input"
                        className="flex-1 min-w-[240px] h-11 px-3 rounded-[10px] border border-[var(--speakup-border)] tabular"
                    />
                    <button
                        onClick={addProof}
                        disabled={!refNo.trim() || busy}
                        data-testid="add-proof-btn"
                        className="h-11 px-4 rounded-[10px] text-white font-semibold disabled:opacity-60"
                        style={{ background: "var(--speakup-red)" }}
                    >
                        Save reference
                    </button>
                </div>
            </div>
        );
    }
    return (
        <div className="flex items-center gap-2 text-[var(--speakup-resolved)]" data-testid="have-sent-verified">
            <Check size={18} className="speakup-check" />
            <div className="font-semibold">
                Sent · Ref <span className="tabular">{refNo || complaint.proof_of_filing_ref}</span> on file.
                Awaiting reply.
            </div>
        </div>
    );
}

function EscalateCard({ complaint, entity, onChanged }) {
    const next = complaint.current_level + 1;
    const [busy, setBusy] = useState(false);
    const nextLabel = LEVEL_LABELS[next];
    const escalate = async () => {
        setBusy(true);
        try {
            await api.post(`/complaints/${complaint.id}/escalate`);
            toast.success(`Drafted for ${nextLabel}`);
            await onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not escalate");
        } finally {
            setBusy(false);
        }
    };
    return (
        <Action
            icon={<ArrowRight size={20} />}
            title={`Escalate to ${nextLabel}`}
            body={`The bank has had its window. We'll draft a fresh complaint to ${nextLabel}, referencing your earlier reference number and dates.`}
            cta={busy ? "Drafting…" : `Escalate now`}
            onClick={escalate}
            testId="action-escalate"
        />
    );
}

function ComposeCard({ composing, onClose }) {
    const copyAll = async () => {
        const text = `To: ${composing.recipient}\nSubject: ${composing.subject}\n\n${composing.body}`;
        await navigator.clipboard.writeText(text);
        toast.success("Copied");
    };
    return (
        <div className="mt-5 speakup-card p-6" data-testid="compose-card">
            <div className="display text-[20px]">Send from your email</div>
            <div className="mt-3 text-[14px] grid gap-2">
                <div><span className="text-[var(--speakup-text-muted)] uppercase text-[11px] tracking-wider mr-2">TO</span>{composing.recipient}</div>
                <div><span className="text-[var(--speakup-text-muted)] uppercase text-[11px] tracking-wider mr-2">BCC</span><span className="tabular">{composing.bcc_alias}</span></div>
                <div><span className="text-[var(--speakup-text-muted)] uppercase text-[11px] tracking-wider mr-2">SUBJECT</span>{composing.subject}</div>
            </div>
            <div className="mt-4 grid md:grid-cols-3 gap-3">
                <a href={composing.gmail_url} target="_blank" rel="noreferrer" data-testid="compose-gmail-btn"
                   className="h-12 inline-flex items-center justify-center rounded-[12px] text-white font-semibold"
                   style={{ background: "var(--speakup-red)" }}>Open Gmail</a>
                <a href={composing.mailto_url} data-testid="compose-mail-btn"
                   className="h-12 inline-flex items-center justify-center rounded-[12px] border-2 font-semibold"
                   style={{ borderColor: "var(--speakup-red)", color: "var(--speakup-red)" }}>Open my email</a>
                <button onClick={copyAll} data-testid="compose-copy-btn"
                        className="h-12 inline-flex items-center justify-center rounded-[12px] border border-[var(--speakup-border)] font-semibold">
                    Copy
                </button>
            </div>
            <button onClick={onClose} className="mt-4 text-sm text-[var(--speakup-text-muted)] underline" data-testid="compose-close">
                I've sent it — refresh
            </button>
        </div>
    );
}

function Timeline({ complaint }) {
    const levels = [1, 2, 3, 4];
    return (
        <div className="mt-7" data-testid="complaint-timeline">
            <h2 className="display text-[20px] mb-4">Escalation timeline</h2>
            <ol className="relative pl-6">
                <span className="absolute left-[10px] top-1.5 bottom-1.5 w-px bg-[var(--speakup-border)]" />
                {levels.map((lvl) => {
                    const hist = (complaint.level_history || []).find((h) => h.level === lvl);
                    const isCurrent = complaint.current_level === lvl && complaint.status !== "resolved" && complaint.status !== "closed";
                    const done = !!hist;
                    const future = lvl > complaint.current_level && !done;
                    return (
                        <li key={lvl} className="relative pb-6" data-testid={`timeline-step-${lvl}`}>
                            <span
                                className={`absolute -left-[3px] top-1 h-4 w-4 rounded-full ${
                                    done ? "bg-[var(--speakup-resolved)]" : isCurrent ? "bg-[var(--speakup-red)]" : "bg-[var(--speakup-border)]"
                                } ring-4 ring-white`}
                            />
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-[var(--speakup-text-strong)]">
                                    L{lvl} · {LEVEL_LABELS[lvl]}
                                </span>
                                {done && <span className="chip chip-resolved">Sent · Ref {hist.reference_no || "—"}</span>}
                                {isCurrent && !done && <span className="chip chip-action">Current</span>}
                                {future && <span className="chip chip-muted">Not yet</span>}
                                {done && hist.verdict && <span className="chip chip-muted">Reply: {hist.verdict}</span>}
                            </div>
                            {hist && (
                                <div className="text-[13px] text-[var(--speakup-text-muted)] mt-1 tabular">
                                    Sent {fmtDate(hist.sent_at)}
                                </div>
                            )}
                            {lvl === 4 && complaint.status === "regulator_eligible" && (
                                <div className="text-[13px] mt-1 text-[var(--speakup-regulator)] font-semibold">
                                    Ready to file with RBI — use the co-pilot button below.
                                </div>
                            )}
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}

function ReplyBox({ complaint, onChanged }) {
    const [show, setShow] = useState(false);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async () => {
        if (!text.trim()) return;
        setBusy(true);
        try {
            const r = await api.post(`/complaints/${complaint.id}/reply`, { text });
            setResult(r.data.verdict);
            await onChanged();
            toast.success("Reply classified");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-7 speakup-card p-5 md:p-6" data-testid="reply-box">
            <div className="flex items-center justify-between">
                <div>
                    <div className="display text-[18px]">Got a reply from the bank?</div>
                    <div className="text-[14px] text-[var(--speakup-text-muted)] mt-1">
                        Paste the reply here — we'll read it and figure out the next step.
                    </div>
                </div>
                <button
                    onClick={() => setShow(!show)}
                    className="text-sm font-semibold text-[var(--speakup-red)]"
                    data-testid="reply-toggle-btn"
                >
                    <CornerDownRight size={14} className="inline mr-1" />
                    {show ? "Hide" : "Paste reply"}
                </button>
            </div>
            {show && (
                <div className="mt-3">
                    <textarea
                        rows={6}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Dear Customer, we have received your complaint. Reference number XYZ123…"
                        data-testid="reply-textarea"
                        className="w-full p-3 rounded-[10px] border border-[var(--speakup-border)]"
                    />
                    <div className="mt-3 flex justify-end">
                        <button
                            onClick={submit}
                            disabled={busy || !text.trim()}
                            data-testid="reply-submit-btn"
                            className="h-11 px-4 rounded-[10px] text-white font-semibold disabled:opacity-60"
                            style={{ background: "var(--speakup-red)" }}
                        >
                            {busy ? "Reading…" : "Classify reply"}
                        </button>
                    </div>
                    {result && (
                        <div className="mt-4 p-4 rounded-[10px] bg-[var(--speakup-surface-muted)] text-sm" data-testid="reply-result">
                            <div className="font-semibold">Verdict: <span className="uppercase">{result.verdict}</span></div>
                            {result.reference_no && <div>Reference: <span className="tabular">{result.reference_no}</span></div>}
                            <div className="text-[var(--speakup-text-muted)] mt-1">{result.summary}</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
